import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { api } from '../utils/api';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Pension {
  id: number;
  code: string;
  name: string;
  heidi: number;
  omar: number;
  totalContributions: number;
  currentValue: number;
}

export const Pensions: React.FC = () => {
  const [pensions, setPensions] = useState<Pension[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchPensions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/pensions');
      const rows: Pension[] = response.data?.pensions || [];
      setPensions(rows);
    } catch (err) {
      setError('Failed to load pensions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPensions();
  }, []);

  const updateField = (id: number, field: keyof Pension, value: number) => {
    setPensions(prev => prev.map(p => {
      if (p.id !== id) return p;
      const next = { ...p, [field]: value };
      if (field === 'heidi' || field === 'omar') {
        next.totalContributions = Number((next.heidi + next.omar).toFixed(2));
      }
      return next;
    }));
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      await Promise.all(
        pensions.map(p => api.put(`/api/pensions/${p.id}`, {
          heidi: p.heidi,
          omar: p.omar,
          totalContributions: p.totalContributions,
          currentValue: p.currentValue
        }))
      );
      setMessage('Pensions saved successfully');
      await fetchPensions();
    } catch (err) {
      setError('Failed to save pensions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Pensions</Typography>
          <Button variant="contained" onClick={saveAll} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

        <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
          <Table size="small" sx={FIT_TABLE_SX}>
            <FitColgroup
              cols={buildFitColgroup([
                { key: 'pension' },
                { key: 'heidi', pct: 18 },
                { key: 'omar', pct: 18 },
                { key: 'total', pct: 18 },
                { key: 'current', pct: 18 },
              ])}
            />
            <TableHead>
              <TableRow>
                <TableCell>Pension</TableCell>
                <TableCell align="right">Heidi Contribution</TableCell>
                <TableCell align="right">Omar Contribution</TableCell>
                <TableCell align="right">Total Contributions</TableCell>
                <TableCell align="right">Current Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pensions.map((pension) => (
                <TableRow key={pension.id}>
                  <TableCell>
                    <Typography variant="body2" sx={fitTruncateSx} title={pension.name}>
                      {pension.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={pension.heidi}
                      onChange={(e) => updateField(pension.id, 'heidi', Number(e.target.value || 0))}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={pension.omar}
                      onChange={(e) => updateField(pension.id, 'omar', Number(e.target.value || 0))}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={pension.totalContributions}
                      onChange={(e) => updateField(pension.id, 'totalContributions', Number(e.target.value || 0))}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={pension.currentValue}
                      onChange={(e) => updateField(pension.id, 'currentValue', Number(e.target.value || 0))}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};
